import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Download, Search, Bug, AlertTriangle, CheckCircle2, Server, Monitor, ChevronDown, ChevronRight, Layers } from "lucide-react";
import { PageAbout } from "@/components/page-about";
import type { ErrorEvent } from "@shared/schema";

interface ErrorStats {
  last24h: number;
  last7d: number;
  unresolvedCount: number;
  topFingerprints: { fingerprint: string; message: string; count: number }[];
}

interface ErrorListResponse {
  rows: ErrorEvent[];
  total: number;
  unresolvedCount: number;
}

function LevelBadge({ level }: { level: string }) {
  const variants: Record<string, "destructive" | "secondary" | "outline"> = {
    error: "destructive",
    warning: "secondary",
    info: "outline",
  };
  return (
    <Badge variant={variants[level] || "outline"} className="capitalize text-[10px]" data-testid={`badge-level-${level}`}>
      {level}
    </Badge>
  );
}

function SourceBadge({ source }: { source: string }) {
  return (
    <Badge variant="outline" className="text-[10px] gap-1" data-testid={`badge-source-${source}`}>
      {source === "server" ? <Server className="h-2.5 w-2.5" /> : <Monitor className="h-2.5 w-2.5" />}
      {source}
    </Badge>
  );
}

function EnvBadge({ env }: { env: string }) {
  return (
    <Badge variant={env === "production" ? "destructive" : "secondary"} className="text-[10px]">
      {env}
    </Badge>
  );
}

function RowDetail({ row }: { row: ErrorEvent }) {
  return (
    <div className="p-4 space-y-4 bg-muted/40 rounded-md text-sm">
      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-1">Full Message</div>
        <p className="break-all">{row.message}</p>
      </div>
      {row.stack && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">Stack Trace</div>
          <pre className="whitespace-pre-wrap break-all text-xs bg-background border border-border rounded-md p-3 overflow-auto max-h-64">{row.stack}</pre>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">URL</div>
          <p className="break-all text-xs">{row.url || "—"}</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">User Agent</div>
          <p className="break-all text-xs">{row.userAgent || "—"}</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">Method / Status</div>
          <p className="text-xs">{row.method || "—"} {row.statusCode || ""}</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">User ID</div>
          <p className="text-xs">{row.userId || "—"}</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">Fingerprint</div>
          <p className="font-mono text-xs">{row.fingerprint}</p>
        </div>
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">Occurred At</div>
          <p className="text-xs">{row.occurredAt ? new Date(row.occurredAt).toLocaleString() : "—"}</p>
        </div>
      </div>
      {row.metadata && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">Metadata</div>
          <pre className="whitespace-pre-wrap break-all text-xs bg-background border border-border rounded-md p-3 overflow-auto max-h-48">{JSON.stringify(row.metadata, null, 2)}</pre>
        </div>
      )}
      {row.resolved && (
        <div className="border-t border-border pt-3 space-y-1">
          <div className="text-xs font-semibold text-muted-foreground">Resolution</div>
          <p className="text-xs">By: {row.resolvedBy || "—"}</p>
          {row.resolvedAt && <p className="text-xs">At: {new Date(row.resolvedAt).toLocaleString()}</p>}
          {row.resolutionNote && <p className="text-xs">Note: {row.resolutionNote}</p>}
        </div>
      )}
    </div>
  );
}

interface ResolveDialogProps {
  row: ErrorEvent | null;
  onClose: () => void;
  onConfirm: (note: string) => void;
  isPending: boolean;
}

function ResolveDialog({ row, onClose, onConfirm, isPending }: ResolveDialogProps) {
  const [note, setNote] = useState("");
  return (
    <Dialog open={!!row} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark as Resolved</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground line-clamp-2">{row?.message}</p>
          <div>
            <label className="text-sm font-medium">Resolution Note (optional)</label>
            <Textarea
              className="mt-1.5"
              placeholder="What fixed this error?"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              data-testid="input-resolution-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending} data-testid="button-cancel-resolve">Cancel</Button>
          <Button onClick={() => onConfirm(note)} disabled={isPending} data-testid="button-confirm-resolve">
            {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Mark Resolved
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function exportErrors(filters: Record<string, string>): Promise<void> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
  const response = await fetch(`/api/admin/errors/export.csv?${params}`, { credentials: "include" });
  if (!response.ok) throw new Error("Export failed");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `errors-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

type GroupedRow = ErrorEvent & { _count: number; _rows: ErrorEvent[] };

export default function AdminErrors() {
  usePageTitle("Error Tracker", "Monitor and manage application errors.");
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [level, setLevel] = useState("all");
  const [resolved, setResolved] = useState("false");
  const [groupBy, setGroupBy] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = useState<ErrorEvent | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (source !== "all") p.source = source;
    if (level !== "all") p.level = level;
    if (resolved !== "all") p.resolved = resolved;
    if (search) p.search = search;
    return p;
  }, [source, level, resolved, search]);

  const queryString = useMemo(() => new URLSearchParams(queryParams).toString(), [queryParams]);

  const { data: stats } = useQuery<ErrorStats>({
    queryKey: ["/api/admin/errors/stats"],
    refetchInterval: 60000,
  });

  const { data: errorData, isLoading } = useQuery<ErrorListResponse>({
    queryKey: ["/api/admin/errors", queryString],
    queryFn: () => fetch(`/api/admin/errors?${queryString}&limit=500`, { credentials: "include" }).then(r => r.json()),
    refetchInterval: 30000,
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiRequest("PATCH", `/api/admin/errors/${id}`, { resolved: true, resolutionNote: note || null }),
    onSuccess: () => {
      toast({ title: "Marked resolved", description: "Error has been marked as resolved." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/errors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/errors/stats"] });
      setResolveTarget(null);
    },
    onError: () => {
      toast({ title: "Failed", description: "Could not mark as resolved.", variant: "destructive" });
    },
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportErrors(queryParams);
      toast({ title: "Exported", description: "Error events exported to CSV." });
    } catch {
      toast({ title: "Export failed", variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  const rows = errorData?.rows || [];

  const displayRows: (ErrorEvent | GroupedRow)[] = useMemo(() => {
    if (!groupBy) return rows;
    const map = new Map<string, GroupedRow>();
    for (const row of rows) {
      const existing = map.get(row.fingerprint);
      if (existing) {
        existing._count++;
        existing._rows.push(row);
        if (new Date(row.occurredAt) > new Date(existing.occurredAt)) {
          existing.occurredAt = row.occurredAt;
        }
      } else {
        map.set(row.fingerprint, { ...row, _count: 1, _rows: [row] });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
    );
  }, [rows, groupBy]);

  const isGrouped = (r: any): r is GroupedRow => groupBy && r._count !== undefined;

  return (
    <div className="space-y-6">
      <PageAbout
        title="Error Tracker"
        description="Real-time view of application errors captured from server and client. Use filters to triage issues, mark resolved errors, and export for offline analysis."
        features={[
          "Captures server exceptions and client-side React/JS errors",
          "Group identical errors by fingerprint to reduce noise",
          "Mark errors resolved with optional resolution notes",
          "SMTP alerts fire when error volume spikes above threshold",
          "Auto-pruned: resolved after 30 days, unresolved after 90 days",
        ]}
        tips={[
          "Toggle 'Group by fingerprint' to see unique error types",
          "Use the 'Resolved' filter to focus on open issues",
          "Export filtered results for offline triage",
        ]}
      />

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-1">
              <CardTitle className="text-xs font-medium text-muted-foreground">Last 24h</CardTitle>
              <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-last24h">{stats.last24h}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-1">
              <CardTitle className="text-xs font-medium text-muted-foreground">Last 7 days</CardTitle>
              <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-last7d">{stats.last7d}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-1">
              <CardTitle className="text-xs font-medium text-muted-foreground">Unresolved</CardTitle>
              <Bug className="h-3.5 w-3.5 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive" data-testid="stat-unresolved">{stats.unresolvedCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 pb-1">
              <CardTitle className="text-xs font-medium text-muted-foreground">Top Type (7d)</CardTitle>
              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium truncate" title={stats.topFingerprints[0]?.message}>
                {stats.topFingerprints[0]?.count
                  ? `${stats.topFingerprints[0].count}× ${stats.topFingerprints[0].message.slice(0, 30)}`
                  : "—"}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search messages..."
            className="pl-8"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="input-error-search"
          />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-32" data-testid="select-source">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="server">Server</SelectItem>
            <SelectItem value="client">Client</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-28" data-testid="select-level">
            <SelectValue placeholder="Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
        <Select value={resolved} onValueChange={setResolved}>
          <SelectTrigger className="w-32" data-testid="select-resolved">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="false">Unresolved</SelectItem>
            <SelectItem value="true">Resolved</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={groupBy ? "default" : "outline"}
          onClick={() => setGroupBy(g => !g)}
          className="gap-1.5"
          data-testid="button-group-fingerprint"
        >
          <Layers className="h-4 w-4" />
          Group by fingerprint
        </Button>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={isExporting}
          className="gap-1.5"
          data-testid="button-export-errors"
        >
          {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </Button>
      </div>

      <div className="text-sm text-muted-foreground">
        {isLoading ? "Loading…" : `${errorData?.total ?? 0} total · ${errorData?.unresolvedCount ?? 0} unresolved`}
        {groupBy && displayRows.length !== rows.length ? ` · ${displayRows.length} unique fingerprints` : ""}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : displayRows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Bug className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No errors found matching the current filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {displayRows.map((row) => {
            const rowId = row.id;
            const expanded = expandedId === rowId;
            const count = isGrouped(row) ? row._count : 1;
            return (
              <Card key={rowId} className={row.resolved ? "opacity-70" : ""} data-testid={`card-error-${rowId}`}>
                <div
                  className="flex flex-wrap items-start gap-2 p-4 cursor-pointer hover-elevate"
                  onClick={() => setExpandedId(expanded ? null : rowId)}
                  data-testid={`row-error-${rowId}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-center flex-shrink-0">
                    <LevelBadge level={row.level} />
                    <SourceBadge source={row.source} />
                    <EnvBadge env={row.environment} />
                    {count > 1 && (
                      <Badge variant="secondary" className="text-[10px]" data-testid={`badge-count-${rowId}`}>
                        ×{count}
                      </Badge>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium truncate"
                      title={row.message}
                      data-testid={`text-error-message-${rowId}`}
                    >
                      {row.message.length > 80 ? row.message.slice(0, 80) + "…" : row.message}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-0.5 text-xs text-muted-foreground">
                      <span>{new Date(row.occurredAt).toLocaleString()}</span>
                      <span className="font-mono">{row.fingerprint.slice(0, 8)}</span>
                      {row.url && <span className="truncate max-w-[200px]" title={row.url}>{row.url}</span>}
                      {row.userId && <span>user:{row.userId.slice(0, 8)}</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {row.resolved ? (
                      <Badge variant="outline" className="text-[10px] gap-1 text-green-600 border-green-600/40" data-testid={`badge-resolved-${rowId}`}>
                        <CheckCircle2 className="h-2.5 w-2.5" /> Resolved
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={e => { e.stopPropagation(); setResolveTarget(row); }}
                        data-testid={`button-resolve-${rowId}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
                {expanded && (
                  <div className="px-4 pb-4">
                    <RowDetail row={row} />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ResolveDialog
        row={resolveTarget}
        onClose={() => setResolveTarget(null)}
        onConfirm={(note) => {
          if (resolveTarget) resolveMutation.mutate({ id: resolveTarget.id, note });
        }}
        isPending={resolveMutation.isPending}
      />
    </div>
  );
}
